import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCreditBalance } from '../services/creditsService';
import { formatBtcAmount } from '../services/bitcoinService';
import Button from '../components/Button';
import { getCart, removeFromCart, updateCartItemQuantity, clearCart, CartItem } from '../services/cartService';
import { isAuthenticated } from '../services/authService';

const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [btcPrice] = useState<number>(60000); // Default BTC price
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate('/auth', { state: { from: '/cart' } });
      return;
    }

    // Load cart and credit balance
    const loadData = async () => {
      try {
        setIsLoading(true);
        const cartData = await getCart();
        setCartItems(cartData.items);
        setTotalAmount(cartData.total);

        const balance = await getCreditBalance();
        setCreditBalance(balance);
      } catch (error) {
        console.error("Error loading cart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    // Set up interval to refresh data
    const intervalId = setInterval(loadData, 5000);
    return () => clearInterval(intervalId);
  }, [navigate]);

  // Calculate BTC equivalent
  const getBtcEquivalent = (amount: number): string => {
    return formatBtcAmount(amount / btcPrice);
  };

  // Remove item from cart
  const handleRemoveFromCart = async (productId: number) => {
    try {
      const updatedCart = await removeFromCart(productId);
      setCartItems(updatedCart.items);
      setTotalAmount(updatedCart.total);
    } catch (error) {
      console.error("Error removing item:", error);
    }
  };

  // Handle quantity change
  const handleQuantityChange = async (productId: number, quantity: number) => {
    try {
      const updatedCart = await updateCartItemQuantity(productId, quantity);
      setCartItems(updatedCart.items);
      setTotalAmount(updatedCart.total);
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  // Clear cart
  const handleClearCart = async () => {
    try {
      await clearCart();
      setCartItems([]);
      setTotalAmount(0);
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  // Check if the user has enough credits
  const hasEnoughCredits = creditBalance >= totalAmount;

  const handleCheckout = () => {
    if (!hasEnoughCredits) {
      // Redirect to top-up page if insufficient credits
      navigate('/topup');
      return;
    }

    // Redirect to checkout page with cart data
    navigate('/checkout/credits', {
      state: {
        items: cartItems,
        total: totalAmount
      }
    });
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="container mx-auto py-12 px-4 flex justify-center items-center h-64">
        <div className="text-white text-lg">Loading your cart...</div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden" style={{
      background: 'linear-gradient(to bottom, #141019, #1A1625)',
    }}>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/10 blur-[150px] rounded-full"></div>

      <div className="container mx-auto py-12 px-4 relative z-10">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-3xl font-bold mb-8 text-white"
        >
          Your Cart
        </motion.h1>

        {cartItems.length === 0 ? (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-8 text-center border border-white/5"
          >
            <div className="text-white/40 text-6xl mb-4">🛒</div>
            <h2 className="text-2xl font-semibold mb-2 text-white">Your cart is empty</h2>
            <p className="text-white/70 mb-6">Looks like you haven't added any items to your cart yet.</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/"
                className="inline-block bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white px-6 py-3 rounded-md hover:opacity-90 transition duration-300"
              >
                Continue Shopping
              </Link>
            </motion.div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={sectionVariants}
                className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg overflow-hidden border border-white/5"
              >
                <table className="min-w-full divide-y divide-white/10">
                  <thead className="bg-[#1A1625]/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">Seller</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-white/60 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-white/60 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#1A1625]/20 divide-y divide-white/10">
                    {cartItems.map(item => (
                      <tr key={item.productId} className="hover:bg-white/5">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-16 w-16">
                              <img className="h-16 w-16 rounded border border-white/10" src={item.product.image} alt={item.product.name} />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">{item.product.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-white/70">{item.product.seller.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={() => handleQuantityChange(item.productId, Math.max(1, item.quantity - 1))}
                              className="px-2 py-1 bg-[#1A1625]/50 rounded-l-md text-white/80 hover:bg-[#1A1625] transition-colors"
                            >
                              -
                            </button>
                            <span className="w-10 text-center py-1 bg-[#1A1625]/30 text-white">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(item.productId, item.quantity + 1)}
                              className="px-2 py-1 bg-[#1A1625]/50 rounded-r-md text-white/80 hover:bg-[#1A1625] transition-colors"
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="text-white/80">{(item.product.price * item.quantity).toFixed(0)} Credits</div>
                          <div className="text-xs text-[#F7931A]">≈ ₿ {getBtcEquivalent(item.product.price * item.quantity)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleRemoveFromCart(item.productId)}
                            className="text-red-400 hover:text-red-300"
                          >
                            Remove
                          </motion.button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>

              <div className="mt-6 flex justify-between">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/"
                    className="inline-flex items-center px-4 py-2 border border-white/10 rounded-md text-sm font-medium text-white/80 bg-[#1A1625]/30 hover:bg-[#1A1625]/50"
                  >
                    ← Continue Shopping
                  </Link>
                </motion.div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClearCart}
                  className="inline-flex items-center px-4 py-2 border border-white/10 rounded-md text-sm font-medium text-red-400 bg-[#1A1625]/30 hover:bg-[#1A1625]/50"
                >
                  Clear Cart
                </motion.button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={sectionVariants}
                className="bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg p-6 border border-white/5"
              >
                <h2 className="text-lg font-semibold text-white mb-4">Order Summary</h2>

                <div className="border-t border-white/10 pt-4 pb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-white/70">Subtotal</span>
                    <span className="text-white">{totalAmount.toFixed(0)} Credits</span>
                  </div>
                  <div className="flex items-center justify-between mb-2 mt-3">
                    <span className="text-white/70">BTC Equivalent</span>
                    <div className="flex items-center">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-[#F7931A] mr-1" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.83 10.94C8.6445 8.744 10.1535 7.832 12.05 7.44L12.05 5.5H13.45L13.45 7.4C13.856 7.4 14.272 7.426 14.69 7.454L14.69 8.83C14.352 8.798 13.994 8.776 13.64 8.776C13.176 8.776 12.735 8.808 12.328 8.844L12.328 12.03C14.642 12.422 16.35 13.034 16.35 15.033C16.35 17.054 14.75 18.084 12.7 18.336L12.7 20.5H11.3L11.3 18.35C10.792 18.35 10.27 18.322 9.75 18.28L9.75 16.88C10.118 16.916 10.54 16.934 10.96 16.934C11.614 16.934 12.17 16.898 12.644 16.844L12.644 13.39C10.384 13.008 8.83 12.318 8.83 10.94ZM12.41 8.956C11.428 9.124 10.78 9.598 10.892 10.838C10.95 11.346 11.382 11.7 12.41 11.976L12.41 8.956ZM13.164 16.77C14.236 16.626 14.9 16.034 14.84 14.998C14.786 14.106 14.222 13.674 13.164 13.408L13.164 16.77Z"/>
                      </svg>
                      <span className="text-[#F7931A] font-mono">{getBtcEquivalent(totalAmount)}</span>
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <div className="flex justify-between">
                      <span className="font-semibold text-white">Your Balance</span>
                      <span className="text-white">{creditBalance.toFixed(0)} Credits</span>
                    </div>
                    {!hasEnoughCredits && (
                      <div className="flex justify-between mt-2">
                        <span className="text-red-400 text-sm">Insufficient credits</span>
                        <Link to="/topup" className="text-sm text-[#7F56D9] hover:underline">
                          Top up
                        </Link>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={!hasEnoughCredits || cartItems.length === 0}
                  onClick={handleCheckout}
                >
                  {hasEnoughCredits ? 'Proceed to Checkout' : 'Add Credits to Continue'}
                </Button>

                <div className="mt-4 text-xs text-center text-[#A3A3A3] flex items-center justify-center">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-[#F7931A] mr-2" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.83 10.94C8.6445 8.744 10.1535 7.832 12.05 7.44L12.05 5.5H13.45L13.45 7.4C13.856 7.4 14.272 7.426 14.69 7.454L14.69 8.83C14.352 8.798 13.994 8.776 13.64 8.776C13.176 8.776 12.735 8.808 12.328 8.844L12.328 12.03C14.642 12.422 16.35 13.034 16.35 15.033C16.35 17.054 14.75 18.084 12.7 18.336L12.7 20.5H11.3L11.3 18.35C10.792 18.35 10.27 18.322 9.75 18.28L9.75 16.88C10.118 16.916 10.54 16.934 10.96 16.934C11.614 16.934 12.17 16.898 12.644 16.844L12.644 13.39C10.384 13.008 8.83 12.318 8.83 10.94ZM12.41 8.956C11.428 9.124 10.78 9.598 10.892 10.838C10.95 11.346 11.382 11.7 12.41 11.976L12.41 8.956ZM13.164 16.77C14.236 16.626 14.9 16.034 14.84 14.998C14.786 14.106 14.222 13.674 13.164 13.408L13.164 16.77Z"/>
                  </svg>
                  <span>Payments to sellers are held for 7 days for buyer protection</span>
                </div>

                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-white/80 mb-2">Secure Checkout</h3>
                  <p className="text-xs text-white/50">
                    We use industry-standard encryption to protect your personal information.
                    All transactions are secure and your data is never shared with third parties.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
